import React, { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Stack } from 'braid-design-system';

import {
  JobTitle,
  type JobTitleChangeHandler,
  type JobTitleChangeEvent,
} from 'src/App/components/JobTitle/JobTitle';
import { JobLocation } from '@seek/job-location';
import type { JobLocationChangeHandler } from '@seek/job-location';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { JobCategory, type JobCategoryChangeHandler } from '@seek/job-category';
import { useAppContext } from 'src/App/context/AppContext';
import { type Locale, getLanguageFromLocale } from '@seek/melways-sites';
import { AdPricingDisclaimer } from '../AdPricingDisclaimer/AdPricingDisclaimer';

interface PriceLookUpProps {
  onJobTitleChange: JobTitleChangeHandler;
  onLocationChange: JobLocationChangeHandler;
  onCategoryChange: JobCategoryChangeHandler;
  advertiserId?: string;
}

export const PriceLookUpInputs = ({
  onJobTitleChange,
  onLocationChange,
  onCategoryChange,
  advertiserId,
}: PriceLookUpProps) => {
  const { t } = useTranslations(translations);
  const [jobTitle, setJobTitle] = useState('');
  const [debouncedJobTitle] = useDebounce(jobTitle, 1000);
  const { language } = useAppContext();

  const onJobTitleChangeInternal = (event: JobTitleChangeEvent): void => {
    if (event.isValid) {
      setJobTitle(event.value);
    }
    onJobTitleChange(event);
  };

  return (
    <Stack space={'large'}>
      <AdPricingDisclaimer />
      <JobTitle
        id="jobTitle"
        label={t('Title')}
        isValidationRequested={true}
        onChange={onJobTitleChangeInternal}
      />
      <JobCategory
        jobTitle={debouncedJobTitle}
        onCategoryChange={onCategoryChange}
        onSubCategoryChange={onCategoryChange}
        onSuggestedCategoryChange={onCategoryChange}
        language={language}
      />
      <JobLocation
        id="JobLocation"
        advertiserId={advertiserId}
        label={t('Location')}
        secondaryLabel={t('suburb or postcode')}
        onChange={onLocationChange}
        language={language}
      />
    </Stack>
  );
};
